import React, { useState } from 'react'
import { FaFolder, FaHouse } from 'react-icons/fa6'
import { useTheme } from 'styled-components'
import { api } from '../../services/api'

interface TreeNodeProps {
  folder: any
  level?: number
  onNameClick: (nodeData: any) => void
  expandedFolders: Set<string>
  folderChildren: Map<string, any[]>
  loadRelatedDocuments: (folderId: string, folderName: string) => void
  selectedFolderId?: string | null
}

const TreeNode: React.FC<TreeNodeProps> = ({ 
  folder, 
  level = 0, 
  onNameClick, 
  expandedFolders, 
  folderChildren, 
  loadRelatedDocuments,
  selectedFolderId
}) => {
  const theme: any = useTheme()
  const primaryColor = theme?.colors?.primary || '#382b7c'
  const isExpanded = expandedFolders.has(folder._id || folder.id)
  const children = folderChildren.get(folder._id || folder.id) || []
  const isSelected = selectedFolderId === (folder._id || folder.id)
  
  const handleClick = () => {
    onNameClick(folder)
  }
  
  return (
    <div style={{ marginLeft: `${level * 16}px` }}>
      <div
        style={{
          padding: '8px 10px',
          margin: '3px 0',
          borderRadius: '6px',
          cursor: 'pointer',
          transition: 'all 0.15s ease',
          backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.7)' : 'transparent',
          color: isSelected ? primaryColor : '#334155',
          fontWeight: isSelected ? 700 : 500,
          border: isSelected ? '1px solid rgba(0, 0, 0, 0.1)' : '1px solid transparent',
          borderLeft: isSelected ? `4px solid ${primaryColor}` : '4px solid transparent',
          boxShadow: isSelected ? '0 2px 4px rgba(0, 0, 0, 0.06)' : 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '13px',
          userSelect: 'none'
        }}
        onClick={handleClick}
        onMouseEnter={(e) => {
          if (!isSelected) {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.4)'
            e.currentTarget.style.color = '#0f172a'
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected) {
            e.currentTarget.style.backgroundColor = 'transparent'
            e.currentTarget.style.color = '#334155'
          }
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {children.length > 0 && (
            <span style={{
              color: '#94a3b8',
              fontSize: '11px',
              fontWeight: 'bold',
              width: '12px',
              textAlign: 'center'
            }}>
              {isExpanded ? '▾' : '▸'}
            </span>
          )}
          {folder.name === 'Início' ? (
            <FaHouse 
              style={{ 
                color: isSelected ? primaryColor : '#64748b',
                fontSize: '14px',
              }} 
            />
          ) : (
            <FaFolder 
              style={{ 
                color: '#f59e0b',
                fontSize: '14px',
              }} 
            />
          )}
        </div>
        <span style={{
          fontWeight: isSelected ? 600 : 500,
          fontSize: '13px',
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {folder.name}
        </span>
        {children.length > 0 && (
          <span style={{
            color: '#94a3b8',
            fontSize: '11px',
            fontWeight: 500
          }}>
            ({children.length})
          </span>
        )}
      </div>
      
      {/* Renderizar filhos se a pasta estiver expandida */}
      {isExpanded && children.length > 0 && (
        <div style={{ marginLeft: '12px' }}>
          {children.map((child: any, index: number) => (
            <TreeNode 
              key={child._id || child.id || index} 
              folder={child} 
              level={level + 1}
              onNameClick={onNameClick}
              expandedFolders={expandedFolders}
              folderChildren={folderChildren}
              loadRelatedDocuments={loadRelatedDocuments}
              selectedFolderId={selectedFolderId}
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface CustomFolderTreeProps {
  treeData: any
  onNameClick: (nodeData: any) => void
  selectedFolderId?: string | null
  style?: React.CSSProperties
}

const CustomFolderTree: React.FC<CustomFolderTreeProps> = ({ 
  treeData, 
  onNameClick, 
  selectedFolderId,
  style 
}) => {
  const theme: any = useTheme()
  const primaryColor = theme?.colors?.primary || '#382b7c'
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())
  const [folderChildren, setFolderChildren] = useState<Map<string, any[]>>(new Map())

  const loadRelatedDocuments = async (folderId: string, folderName: string) => {
    try {
      if (folderChildren.has(folderId)) {
        setExpandedFolders(prev => new Set(prev).add(folderId))
        return
      }
      
      const response = await api.get('/documents', {
        params: {
          page: 1,
          perPage: 5000,
          directory: JSON.stringify(['/', folderId]),
        },
      });
      
      if (response.data?.data) {
        const relatedFolders = response.data.data
          .filter((doc: any) => doc.isFolder === true)
          .sort((a: any, b: any) => {
            return a.name.localeCompare(b.name, 'pt-BR', { 
              sensitivity: 'base',
              numeric: true 
            });
          })
        
        setFolderChildren(prev => new Map(prev).set(folderId, relatedFolders))
        setExpandedFolders(prev => new Set(prev).add(folderId))
      }
    } catch (error) {
      console.error('Erro ao buscar documentos relacionados:', error)
    }
  }

  const handleNameClick = (nodeData: any) => {
    if (nodeData.isFolder) {
      const isExpanded = expandedFolders.has(nodeData._id || nodeData.id)
      if (isExpanded) {
        setExpandedFolders(prev => {
          const newSet = new Set(prev);
          newSet.delete(nodeData._id || nodeData.id);
          return newSet;
        });
      } else {
        loadRelatedDocuments(nodeData._id || nodeData.id, nodeData.name);
      }
      onNameClick(nodeData)
    }
  }

  if (!treeData) {
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center', 
        color: '#94a3b8',
        fontSize: '13px'
      }}>
        Carregando estrutura...
      </div>
    )
  }

  return (
    <div style={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      ...style
    }}>
      <div style={{ 
        flex: 1,
        overflow: 'auto',
      }}>
        {treeData.children && treeData.children.length > 0 ? (
          treeData.children.map((folder: any, index: number) => (
            <TreeNode 
              key={folder._id || folder.id || index} 
              folder={folder} 
              level={0}
              onNameClick={handleNameClick}
              expandedFolders={expandedFolders}
              folderChildren={folderChildren}
              loadRelatedDocuments={loadRelatedDocuments}
              selectedFolderId={selectedFolderId}
            />
          ))
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '24px 12px',
            color: '#94a3b8',
            fontSize: '13px'
          }}>
            Nenhuma pasta encontrada
          </div>
        )}
      </div>
      
      <style>{`
        /* Scroll personalizado */
        ::-webkit-scrollbar {
          width: 5px;
        }
        ::-webkit-scrollbar-track {
          background: transparent;
        }
        ::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  )
}

export default CustomFolderTree
